/**
 * @file studentResponseObjectivesAPI.ts
 * @brief This file contains the API functions for managing student responses for objectives.
 */

//* Import API configurations
import { api } from "../../services/apis/configs/axiosConfigs";

//* Import API URI constant
import { API_URI } from "../../constants/consts";

//* Import PaginationData type
import { type Pagination } from "../../services/stores/types";

//* Import types from types
import {
    GetStudentResponse,
    GetStudentResponseForStudent,
    GetStudentResult,
} from "../studentResponseObjectives/types/types";


/**
 * @brief This object contains the API functions for managing student responses for objectives.
 */
export const studentResponseObjectivesAPI = {
  /**
   * @async
   * @function getAll
   * @brief Fetches all student responses with optional search and pagination.
   *
   * @param {string} search - The search term to filter student responses (optional).
   * @param {number} page - The page number for pagination (default is 1).
   * @param {string} token - The authorization token for the request.
   * @param {number} perPage - The number of items per page (default is 10).
   * @returns {Promise<GetStudentResponse>} A promise that resolves to a GetStudentResponse object containing student responses and pagination data.
   */
  getAll: async function (
    search: string = "",
    page: number = 1,
    token: string,
    perPage: number = 10
  ): Promise<GetStudentResponse> {
    const searchQuery = search ? `&search=${search}` : "";
    const url = `${API_URI}/admin/private/student-responses-objectives?page=${page}&perPage=${perPage}${searchQuery}`;

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { data } = response.data;

        const pagination: Pagination = data.pagination
          ? {
              currentPage: data.pagination.currentPage,
              totalPages: data.pagination.totalPages,
              itemsPerPage: data.pagination.itemsPerPage,
              totalItems: data.pagination.totalItems,
            }
          : {
              currentPage: 0,
              totalPages: 0,
              itemsPerPage: 0,
              totalItems: 0,
            };

        return {
          responses: data.responses || [],
          pagination,
        };
      });
  },

  /**
   * @async
   * @function getAllByStudent
   * @brief Fetches student responses for a specific student by their ID.
   *
   * @param {string} answerSheetId - The ID of the answer sheet.
   * @param {string} token - The authorization token for the request.
   * @param {number} page - The page number for pagination (default is 1).
   * @param {number} perPage - The number of items per page (default is 10).
   * @param {string} search - (Optional) The search term for filtering by name or email.
   * @returns {Promise<GetStudentResponseForStudent>} A promise that resolves to a GetStudentResponseForStudent object containing student responses and pagination data.
   */
  getAllByStudent: async function (
    answerSheetId: string,
    token: string,
    page: number = 1,
    perPage: number = 10,
    search?: string
  ): Promise<GetStudentResponseForStudent> {
    const searchQuery = search ? `&search=${encodeURIComponent(search)}` : "";
    const url = `${API_URI}/admin/private/student-responses-objectives/${answerSheetId}/answer-sheets?page=${page}&perPage=${perPage}${searchQuery}`;


    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { data } = response.data;

        const pagination: Pagination = data.pagination
          ? {
              currentPage: data.pagination.currentPage,
              totalPages: data.pagination.totalPages,
              itemsPerPage: data.pagination.itemsPerPage,
              totalItems: data.pagination.totalItems,
            }
          : {
              currentPage: 0,
              totalPages: 0,
              itemsPerPage: 0,
              totalItems: 0,
            };

        return {
          students: data.students || [],
          pagination,
        };
      });
  },

  /**
   * @async
   * @function getAllStudentResponsesByStudent
   * @brief Fetches all student responses for a specific student.
   *
   * @param {string} answerSheetId - The ID of the answer sheet.
   * @param {string} studentId - The ID of the student.
   * @param {string} token - The authorization token for the request.
   * @param {number} page - The page number for pagination (default is 1).
   * @param {number} perPage - The number of items per page (default is 10).
   * @param {string} status - (Optional) The status to filter student responses.
   * @param {string} search - (Optional) The search term for filtering student responses.
   * @returns {Promise<GetStudentResponse>} A promise that resolves to a GetStudentResponse object containing student responses and pagination data.
   */
  getAllStudentResponsesByStudent: async function (
    answerSheetId: string,
    studentId: string,
    token: string,
    page: number = 1,
    perPage: number = 10,
    status?: string,
    search?: string
  ): Promise<GetStudentResponse> {
    let url = `${API_URI}/admin/private/student-responses-objectives/${answerSheetId}/answer-sheets/student/${studentId}?page=${page}&perPage=${perPage}`;

    if (status) {
      url += `&status=${status}`;
    }

    if (search) {
      url += `&search=${encodeURIComponent(search)}`;
    }

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { data } = response.data;

        const pagination: Pagination = data.pagination
          ? {
              currentPage: data.pagination.currentPage,
              totalPages: data.pagination.totalPages,
              itemsPerPage: data.pagination.itemsPerPage,
              totalItems: data.pagination.totalItems,
            }
          : {
              currentPage: 0,
              totalPages: 0,
              itemsPerPage: 0,
              totalItems: 0,
            };

        return {
          responses: data.responses || [],
          pagination,
        };
      });
  },

  /**
   * @async
   * @function getResults
   * @brief Fetches the aggregated results of each student for a specific answer sheet, allowing filtering by student name.
   *
   * @param {string} answerSheetId - The ID of the answer sheet.
   * @param {string} token - Authorization token.
   * @param {number} page - Current page (default 1).
   * @param {number} perPage - Items per page (default 10).
   * @param {string} search - Search term (optional).
   * @returns {Promise<GetStudentResult>} An object with the results and pagination data.
   */
  getResults: async function (
    answerSheetId: string,
    token: string,
    page: number = 1,
    perPage: number = 10,
    search?: string
  ): Promise<GetStudentResult> {
    const searchQuery = search ? `&search=${encodeURIComponent(search)}` : "";
    const url = `${API_URI}/admin/private/student-responses-objectives/${answerSheetId}/answer-sheets/results?page=${page}&perPage=${perPage}${searchQuery}`;

    return api
      .request({
        url,
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      })
      .then((response) => {
        const { data } = response.data;

        const pagination: Pagination = data.pagination
          ? {
              currentPage: data.pagination.currentPage,
              totalPages: data.pagination.totalPages,
              itemsPerPage: data.pagination.itemsPerPage,
              totalItems: data.pagination.totalItems,
            }
          : {
              currentPage: 0,
              totalPages: 0,
              itemsPerPage: 0,
              totalItems: 0,
            };

        return {
          results: data.results || [],
          pagination,
        };
      });
  },

  /**
   * @async
   * @function updateStudentResponse
   * @brief Updates a student response.
   *
   * @param {string} id - The ID of the student response to update.
   * @param {object} updateData - The data to update.
   * @param {string} token - The authorization token for the request.
   * @returns {Promise<void>}
   */
  //*Update student response
  updateStudentResponse: async function (
    id: string,
    updateData: {is_wrong: boolean},
    token: string,
  ): Promise<void> {
    const url = `${API_URI}/admin/private/student-responses-objectives/${id}`;

    return api
      .request({
        url,
        method: "PUT",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        data: { is_wrong: updateData.is_wrong },
      })
      .then((response) => response.data)
      .catch((error => {
        console.error("Error updating student response:", error);
        throw error;
      })
      )
  }
};